import { useMemo } from 'react'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useSeasonTrinkets } from 'app/frontend/base/config/queries'
import { TrinketChoice } from 'app/frontend/base/api/character'
import UpgradeTrack, {
  upgradeTracks,
} from 'app/frontend/types/custom/UpgradeTrack'
import EquippedItem from 'app/frontend/types/custom/EquippedItem'

export const trinketRanks = ['1', '2', '3'] as const

export type TrinketRank = typeof trinketRanks[number]

export const trinketRankLabels: Record<TrinketRank, string> = {
  '1': '1st',
  '2': '2nd',
  '3': '3rd',
}

export const trinketRankColumnLabels: Record<TrinketRank, string> = {
  '1': '1st choice',
  '2': '2nd choice',
  '3': '3rd choice',
}

export type TrinketItemInfo = {
  id: number
  name: string
  icon: string
  quality: wowaudit.WishlistItem['quality']
  encounterName: string
  eligibleClasses: number[]
}

export type TrinketPriority = Record<string, TrinketChoice>

export type TrinketMemberRow = {
  member: wowaudit.TeamMembership
  storedPriority: TrinketPriority
  trinketCache: Record<string, number> | undefined
  equippedTrinkets: (EquippedItem | undefined)[]
}

export const useTrinketItemLookup = (
  seasonId: number,
): {
  lookup: Record<number, TrinketItemInfo>
  isPending: boolean
} => {
  const { team } = useTeam()
  const { data, isPending } = useSeasonTrinkets(team.id, seasonId)

  const lookup = useMemo(() => {
    const next: Record<number, TrinketItemInfo> = {}
    data?.forEach((trinket) => {
      next[trinket.id] = {
        id: trinket.id,
        name: trinket.name,
        icon: trinket.icon,
        quality: trinket.quality,
        encounterName: trinket.source_name,
        eligibleClasses: trinket.eligible_classes,
      }
    })
    return next
  }, [data])

  return { lookup, isPending }
}

export const trinketChoiceFor = (
  storedPriority: TrinketPriority,
  rank: TrinketRank,
): TrinketChoice => storedPriority[rank] ?? null

export const pickedCount = (storedPriority: TrinketPriority): number =>
  trinketRanks.filter((rank) => storedPriority[rank]?.item_id).length

export const isTrinketAutoObtained = (
  itemId: number,
  cache: Record<string, number> | undefined,
  minimumTrack: UpgradeTrack,
): boolean => {
  const trackIndex = cache?.[String(itemId)]
  if (trackIndex === undefined) return false
  return trackIndex <= upgradeTracks.indexOf(minimumTrack)
}

export const detectedTrinketTrack = (
  itemId: number,
  cache: Record<string, number> | undefined,
): UpgradeTrack | undefined => {
  const trackIndex = cache?.[String(itemId)]
  if (trackIndex === undefined) return undefined
  return upgradeTracks[trackIndex]
}

export const isChoiceObtained = (
  choice: TrinketChoice,
  cache: Record<string, number> | undefined,
  minimumTrack: UpgradeTrack,
): boolean =>
  !!choice &&
  (choice.obtained ||
    isTrinketAutoObtained(choice.item_id, cache, minimumTrack))

export const obtainedCount = (
  storedPriority: TrinketPriority,
  cache: Record<string, number> | undefined,
  minimumTrack: UpgradeTrack,
): number =>
  trinketRanks.filter((rank) =>
    isChoiceObtained(storedPriority[rank] ?? null, cache, minimumTrack),
  ).length
